import type {SlotGameStore, Spine, ReelsetComponent as ReelsetComponentType} from "@gamzix/slot_core";

import {CONST} from "../../../src/const";
import {CoinAnimationComponent} from "../../coinAnimation/src";

class BonusGameComponent extends SlotGame.src.component.BonusGameComponent {
    protected jackpotContainer;
    protected platinumCoinContainer;

    static events = {
        START_INTRO_POPUP: "BonusGameComponent.START_INTRO_POPUP",
        START_HIDE_INTRO_POPUP: "BonusGameComponent.START_HIDE_INTRO_POPUP",
        END_INTRO_POPUP: "BonusGameComponent.END_INTRO_POPUP",
        START_HIDE_END_INTRO_POPUP: "BonusGameComponent.START_HIDE_END_INTRO_POPUP",
        START_OUTRO_POPUP: "BonusGameComponent.START_OUTRO_POPUP",
        END_OUTRO_POPUP: "BonusGameComponent.END_OUTRO_POPUP",
        OUTRO_POPUP_START_COUNT: "BonusGameComponent.OUTRO_POPUP_START_COUNT",
        OUTRO_POPUP_END_COUNT: "BonusGameComponent.OUTRO_POPUP_END_COUNT",
        START_WIN_PLATINUM: "BonusGameComponent.START_WIN_PLATINUM",
        END_WIN_PLATINUM: "BonusGameComponent.END_WIN_PLATINUM",
    }

    public platinumConfig = {
        view: {
            type: "Spine",
            spine: "platinum",
            name: "bonus_game.wrapper_container.jackpot_container.platinum_animation",
            animation: "land",
            position: {x: 0, y: -120},
            loop: false
        },
        animations: {
            START: "land",
            IDLE: "idle",
            END: "end"
        }
    }

    override onGameReady() {
        super.onGameReady();
        this.jackpotContainer = game.tree.findByName('bonus_game.wrapper_container.jackpot_container');
        this.platinumCoinContainer = game.tree.findByName('bonus_game.wrapper_container.jackpot_container.coin_animation');
    }

    constructor(config) {
        super(config);

        this.status_checkers = {
            ...super.getStatusCheckers(),

            isEnglishLang: () => (<SlotGameStore>game.store).language === "en"
        };

        this.text_checkers = {
            ...super.getTextCheckers(),

            GET_TOTAL_WIN_NUMBER: () => this.store.toFormatMoney(this.store.gameData.bonusGame.win)
        };
    }

    async showStartPopup() {
        game.emit("BonusGameComponent.START_INTRO_POPUP");

        const container = game.tree.findByName('bonus_game.wrapper_container.popup_intro_container');

        container.options.visible = true;

        await game.utils.GameUtils.playAlphaAnimation(container, {from: 0, to: 1, time: 500, timeWait: 0});

        await Promise.race([
            game.utils.delay(10000),
            this.waitClick(),
            game.onceDataPromise('Button.clicked', {
                component: 'bonusGame',
                name: 'bonus_game.intro_popup_container.skip_btn'
            })
        ]);

        game.emit(BonusGameComponent.events.START_HIDE_INTRO_POPUP);
        await game.utils.GameUtils.playAlphaAnimation(container, {from: 1, to: 0, time: 500, timeWait: 0});

        game.emit("BonusGameComponent.END_INTRO_POPUP", {});
    }

    async showFinishPopup() {
        game.emit("BonusGameComponent.START_OUTRO_POPUP");

        await this.showOutroPopup();
        
        game.emit("BonusGameComponent.END_OUTRO_POPUP", {});

        // if (!this.gameData.isFreespinGamePopupsInProgress) game.emit("BonusGameComponent.END_OUTRO_POPUP.PlayMainSound");
    }

    async showOutroPopup() {
        const container = game.tree.findByName('bonus_game.wrapper_container.popup_outro_container');
        const totalWin = game.tree.findByName('bonus_game.outro_popup_container.total_win_value');

        container.options.visible = true;

        totalWin.options.text = this.store.toFormatMoney(0);
        totalWin.updateViewOptions();

        await game.utils.GameUtils.playAlphaAnimation(container, {from: 0, to: 1, time: 500, timeWait: 0});

        game.emit(BonusGameComponent.events.OUTRO_POPUP_START_COUNT);

        await Promise.race([
            new Promise<void>(async resolve => {
                await game.utils.GameUtils.playCounterAnimation(totalWin, {
                    from: 0,
                    to: this.store.gameData.bonusGame.win,
                    time: 3000,
                    timeWait: 0,
                    minTimeForUpdate: 0
                });
                game.emit(BonusGameComponent.events.OUTRO_POPUP_END_COUNT);
                await game.utils.delay(10000);
                resolve();
            }),
            this.waitClick(),
            game.onceDataPromise('Button.clicked', {
                component: 'bonusGame',
                name: 'bonus_game.outro_popup_container.skip_btn'
            })
        ]);
        game.emit(BonusGameComponent.events.OUTRO_POPUP_END_COUNT);

        game.utils.removeCall(totalWin._tween);

        totalWin.options.text = this.store.toFormatMoney(this.store.gameData.bonusGame.win);
        totalWin.updateViewOptions();

        await game.utils.delay(700);
        game.emit(BonusGameComponent.events.START_HIDE_END_INTRO_POPUP);
        await game.utils.GameUtils.playAlphaAnimation(container, {from: 1, to: 0, time: 500, timeWait: 0});
    }

    async showJackpotPopup() {
        if (!this.isPlatinumJackpot) return;

        const platinumAnimation = <Spine>game.builder.create(this.platinumConfig.view);
        this.jackpotContainer.addChild(platinumAnimation);

        const playAnimation = async () => {
            await platinumAnimation.playAnimation(this.platinumConfig.animations.START, false);
            platinumAnimation.playAnimation(this.platinumConfig.animations.IDLE, true);
        }

        game.emit(BonusGameComponent.events.START_WIN_PLATINUM);
        game.utils.GameUtils.playAlphaAnimation(this.jackpotContainer, {from: 0, to: 1, time: 300, timeWait: 0});

        this.platinumCoinContainer.options.text = this.store.toFormatMoney(0);
        this.platinumCoinContainer.updateViewOptions();
        this.onChangeOrientation();
        playAnimation();

        await game.utils.GameUtils.playCounterAnimation(this.platinumCoinContainer, {
            from: 0,
            to: this.store.gameData.bet * CONST.JACKPOT.PLATINUM.multiplier,
            time: 6000,
            timeWait: 1000,
            minTimeForUpdate: 40
        })

        platinumAnimation.playAnimation(this.platinumConfig.animations.END, false);
        await game.utils.GameUtils.playAlphaAnimation(this.jackpotContainer, {from: 1, to: 0, time: 600, timeWait: 0});
        game.emit(BonusGameComponent.events.END_WIN_PLATINUM);
    }

    getFSMHandlers(): any {
        return {
            ...super.getFSMHandlers(),
            SPIN: {
                ...super.getFSMHandlers()['SPIN'],
                exit: {
                    on: async () => {
                        if (this.store.gameData.bonusGame.isFinished()) {
                            await Promise.race([
                                game.oncePromise(CoinAnimationComponent.events.ANIMATE_FINISHED)
                            ])
                            await this.showJackpotPopup();
                        }
                    }
                }
            }
        }
    }

    get isPlatinumJackpot() {
        const countCoinsOnLine = [0, 0, 0, 0];

        this.store.gameData.reels.forEach((list) => {
            list.map((el, rowId) => {
                if (ReelsetComponent.src.SymbolView.bonusGameConfig.coinSymbolIds.includes(String(el?.symbol))) {
                    countCoinsOnLine[rowId]++;
                }
            })
        });

        const countFullLines = countCoinsOnLine.filter(el => el == 5).length;

        return countFullLines == 4;
    }

    get reelset(): ReelsetComponentType {
        return <any>game.component.reelset;
    }
}

export {BonusGameComponent};

//@ts-ignore
Game.src.component.BonusGameComponent = BonusGameComponent;